/**
 * MAP (in-file navigation graph — any LLM reads this without external tooling;
 * keep in sync with the code):
 *   role:      controller — online-payment status page and retry-payment flow
 *   extends:   Controller
 *   template:  none
 *   key:       isNotProjectPayed, pagamentoVerificato (getters), retryPayment
 *              (task), sendInvitation (action)
 *   uses:      applicationController (app/controllers/application.js) —
 *              saveRecordRelated, sendInvitation; services: intl, notify,
 *              store, session, log; rsvp `all`, moment-timezone
 *   siblings:  routes/payment-status.js
 *   tests:     tests/unit/controllers/payment-status-test.js,
 *              tests/unit/routes/payment-status-route-test.js,
 *              tests/acceptance/payment-status-test.js
 *   gotchas:   isNotProjectPayed compares `project_state.id` (an Ember Data
 *              string id) against the number `92` — always true regardless of
 *              real state; fixed to compare against the string `'92'`.
 *              retryPayment's `saveRecordRelated` never rejects (resolves
 *              `false` on failure), so its catch block never runs for a
 *              refused save — the task explicitly checks
 *              `paymentSaved === false` and rolls back the unsaved retry
 *              payment itself. `access_token` QP is owned by the application
 *              controller (see application.js queryParams) — do NOT redeclare
 *              it here, two controllers mapping the same QP key throws on
 *              route entry; the deep-link token is read in
 *              routes/payment-status.js beforeModel instead.
 *
 * Online-payment status/retry controller for the e-commerce storefront.
 */
import Controller from '@ember/controller';
import { all } from 'rsvp';
import moment from 'moment-timezone';

import { task } from 'ember-concurrency';

import { action, set } from '@ember/object';
import { tracked } from '@glimmer/tracking';

import { inject as service } from '@ember/service';
import { inject as controller } from '@ember/controller';

import { isPaymentValidForRetry } from 'beebee-online-payments/lib/payment-logic';

export default class PaymentStatusController extends Controller {
  @service intl;

  @service notify;
  @service store;
  @service session;
  @service log;
  @controller('application') applicationController;

  // NOTE: `access_token` QP is owned by the application controller (see
  // application.js `queryParams`). Do NOT redeclare it here — two active
  // controllers mapping the same QP key makes Ember throw on route entry.
  // The deep-link token is read in routes/payment-status.js beforeModel.

  get isNotProjectPayed() {
    // Ember Data ids are strings: `!== 92` (number) was always true, so this
    // getter never reported a project as paid regardless of its real state.
    return this.content?.projects?.[0]?.project_state?.id !== '92';
  }

  get pagamentoVerificato() {
    return !!this.content?.tran_bank_id && !!this.content?.transaction_type;
  }

  @tracked newPayment = null;

  @task({ drop: true }) *retryPayment() {
    if (!this.content?.id) return;
    let newPayment = null;
    try {
      const oldPayment = yield this.store.findRecord('payment', this.content?.id, { reload: true });
      const dateDiff = moment().diff(moment(oldPayment.date), 'minutes');

      if (dateDiff > 15) {
        this.notify.warning(this.intl.t('online_payment.time_expired'));
        return false;
      }

      const isValid = yield isPaymentValidForRetry(oldPayment);
      if (!isValid) {
        this.notify.warning(this.intl.t('online_payment.project_archived'));
        return false;
      }

      newPayment = this.store.createRecord('payment', {
        sent: false,
        date: moment().format('YYYY-MM-DDTHH:mmZ'),
        total: oldPayment.total,
        option_json: oldPayment.option_json,
        option: oldPayment.option,
        voucher: oldPayment.voucher,
        status: oldPayment.status,
      });

      const [oldProjects, oldEvents, oldContacts] = yield all([
        oldPayment.projects,
        oldPayment.events,
        oldPayment.contacts,
      ]);

      const [newProjects, newEvents, newContacts] = yield all([
        newPayment.projects,
        newPayment.events,
        newPayment.contacts,
      ]);

      for (const project of oldProjects.slice()) {
        newProjects.push(project);
      }
      for (const event of oldEvents.slice()) {
        newEvents.push(event);
      }
      for (const contact of oldContacts.slice()) {
        newContacts.push(contact);
      }

      const paymentSaved = yield this.applicationController.saveRecordRelated(newPayment);

      // saveRecordRelated NEVER rejects — it resolves `false` on failure, so the catch
      // below never fires for a refused save and its rollback never ran: the unsaved
      // retry `payment` stayed in the store as an isNew record, ready to be swept into
      // a later related save.
      if (paymentSaved === false) {
        if (!newPayment.isDestroyed && !newPayment.isDestroying) {
          newPayment.rollbackAttributes();
        }
        return;
      }

      if (paymentSaved) {
        //this.store.pushPayload(paymentSaved[0]);
        const savedId = paymentSaved[0]?.data?.id;
        if (savedId) {
          const storeItem = this.store.peekRecord('payment', savedId);
          set(this, 'newPayment', storeItem);
        }
      }
    } catch (error) {
      // Roll back the unsaved retry payment so it doesn't orphan in the store.
      if (newPayment && !newPayment.isDestroyed && !newPayment.isDestroying) {
        newPayment.rollbackAttributes();
      }
      this.log.error('Error retrying payment:', error);
      this.notify.error(this.intl.t('beebeeboard error'));
    }
  }

  @action
  sendInvitation() {
    this.applicationController.sendInvitation.perform();
  }
}
